import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { DatePicker } from '@/components/ui/date-picker';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/hooks/use-toast';
import { useStore, Client } from '@/lib/store';
import { ClientCombobox } from '@/components/common/EntityCombobox';
import {
  listFeeSharingConfigs,
  createFeeSharingConfig,
  updateFeeSharingConfig,
  deleteFeeSharingConfig,
  getFeeSharingStatus,
  resetFixedUsage,
} from '@/services/fee-sharing';
import type {
  FeeSharingConfig,
  FeeSharingConfigPayload,
  FeeSharingStatus,
  FeeSharingType,
} from '@/types/fee-sharing';
import {
  Plus,
  Pencil,
  Trash2,
  Calendar,
  Users,
  TrendingUp,
  AlertTriangle,
  CheckCircle2,
  RotateCcw,
  Info,
  ChevronDown,
  ChevronRight,
  Euro,
  Percent,
  Clock,
  Shield,
} from 'lucide-react';

interface FeeSharingPanelProps {
  matterId: number;
  className?: string;
}

// ─── Helpers ───────────────────────────────────────────────────────────────────

function formatCurrency(value: string | number | null | undefined): string {
  if (value == null) return '—';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return '—';
  return new Intl.NumberFormat('en-BE', { style: 'currency', currency: 'EUR' }).format(num);
}

function formatDate(dateStr: string): string {
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function getClientName(clients: Client[], clientId: number): string {
  return clients.find((c) => c.id === clientId)?.name ?? `Client #${clientId}`;
}

// ─── Main Component ────────────────────────────────────────────────────────────

export function FeeSharingPanel({ matterId, className = '' }: FeeSharingPanelProps) {
  const { toast } = useToast();
  const { clients } = useStore();

  const [configs, setConfigs] = useState<FeeSharingConfig[]>([]);
  const [status, setStatus] = useState<FeeSharingStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedConfig, setExpandedConfig] = useState<number | null>(null);

  // Dialog states
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState<FeeSharingConfig | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<FeeSharingConfig | null>(null);
  const [resetTarget, setResetTarget] = useState<FeeSharingConfig | null>(null);

  // ─── Data Fetching ───────────────────────────────────────────────────────────

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [configList, currentStatus] = await Promise.allSettled([
        listFeeSharingConfigs(matterId),
        getFeeSharingStatus(matterId),
      ]);

      if (configList.status === 'fulfilled') {
        const sorted = [...configList.value].sort(
          (a, b) => new Date(b.effective_from).getTime() - new Date(a.effective_from).getTime()
        );
        setConfigs(sorted);
        // Auto-expand the active one
        const active = sorted.find((c) => c.is_active);
        if (active?.id) setExpandedConfig(active.id);
      }

      if (currentStatus.status === 'fulfilled') {
        setStatus(currentStatus.value);
      } else {
        setStatus(null);
      }
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to load fee sharing configurations',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [matterId, toast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ─── Actions ─────────────────────────────────────────────────────────────────

  const handleCreate = () => {
    setEditingConfig(null);
    setIsFormOpen(true);
  };

  const handleEdit = (config: FeeSharingConfig) => {
    setEditingConfig(config);
    setIsFormOpen(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget?.id) return;
    try {
      await deleteFeeSharingConfig(matterId, deleteTarget.id);
      toast({ title: 'Deleted', description: 'Fee sharing configuration removed.' });
      setDeleteTarget(null);
      fetchData();
    } catch {
      toast({ title: 'Error', description: 'Failed to delete configuration', variant: 'destructive' });
    }
  };

  const handleReset = async () => {
    try {
      const payload = resetTarget?.id ? { config_id: resetTarget.id } : undefined;
      const result = await resetFixedUsage(matterId, payload);
      toast({ title: 'Reset Complete', description: result.message });
      setResetTarget(null);
      fetchData();
    } catch {
      toast({ title: 'Error', description: 'Failed to reset fixed amount usage', variant: 'destructive' });
    }
  };

  const handleFormSuccess = () => {
    setIsFormOpen(false);
    setEditingConfig(null);
    fetchData();
  };

  const toggleExpand = (configId: number | undefined) => {
    if (!configId) return;
    setExpandedConfig((prev) => (prev === configId ? null : configId));
  };

  // ─── Render ──────────────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <Card className={className}>
        <CardContent className="py-12">
          <div className="flex items-center justify-center gap-3 text-muted-foreground">
            <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            <span>Loading fee sharing configurations…</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className={`space-y-5 ${className}`}>
      {/* ── Status Overview ────────────────────────────────────────────────────── */}
      {status && <FeeSharingStatusCard status={status} clients={clients} />}

      {/* ── Configs Header ────────────────────────────────────────────────────── */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Users className="h-5 w-5 text-primary" />
                Fee Sharing
              </CardTitle>
              <CardDescription>
                Manage how billing is split between clients on this matter
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              {configs.some((c) => c.participants?.some((p) => p.fee_sharing_type === 'fixed_amount')) && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setResetTarget({} as FeeSharingConfig)}
                      >
                        <RotateCcw className="h-4 w-4 mr-1.5" />
                        Reset Usage
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Reset all fixed-amount usage counters</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
              {/* The empty state has its own call to action */}
              {configs.length > 0 && (
                <Button size="sm" onClick={handleCreate}>
                  <Plus className="h-4 w-4 mr-1.5" />
                  Add configuration
                </Button>
              )}
            </div>
          </div>
        </CardHeader>

        <CardContent className="pt-0">
          {configs.length === 0 ? (
            <EmptyState onAdd={handleCreate} />
          ) : (
            <div className="space-y-3">
              {configs.map((config) => (
                <ConfigCard
                  key={config.id ?? config.effective_from}
                  config={config}
                  clients={clients}
                  isExpanded={expandedConfig === config.id}
                  onToggle={() => toggleExpand(config.id)}
                  onEdit={() => handleEdit(config)}
                  onDelete={() => setDeleteTarget(config)}
                  onReset={() => setResetTarget(config)}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Dialogs ────────────────────────────────────────────────────────────── */}
      <FeeSharingFormDialog
        isOpen={isFormOpen}
        onClose={() => {
          setIsFormOpen(false);
          setEditingConfig(null);
        }}
        onSuccess={handleFormSuccess}
        matterId={matterId}
        existingConfig={editingConfig}
        clients={clients}
        existingDates={configs.map((c) => c.effective_from)}
      />

      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Fee Sharing Configuration?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove the configuration
              {deleteTarget?.label ? ` "${deleteTarget.label}"` : ''} effective from{' '}
              {deleteTarget?.effective_from ? formatDate(deleteTarget.effective_from) : ''}. This
              action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!resetTarget} onOpenChange={() => setResetTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset Fixed Amount Usage?</AlertDialogTitle>
            <AlertDialogDescription>
              {resetTarget?.id
                ? `This will reset the consumed amounts for all fixed-amount participants in "${resetTarget.label || 'this configuration'}".`
                : 'This will reset the consumed amounts for all fixed-amount participants across every configuration on this matter.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleReset}>Reset Usage</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ─── Status Card ───────────────────────────────────────────────────────────────

function FeeSharingStatusCard({
  status,
}: {
  status: FeeSharingStatus;
  clients: Client[];
}) {
  const isPrimary = status.phase === 'primary';

  return (
    <Card className="border-primary/20 bg-gradient-to-br from-primary/[0.03] to-transparent">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${isPrimary ? 'bg-primary/10 text-primary' : 'bg-amber-500/10 text-amber-600'}`}>
              {isPrimary ? <TrendingUp className="h-5 w-5" /> : <AlertTriangle className="h-5 w-5" />}
            </div>
            <div>
              <CardTitle className="text-base">
                Current Status:{' '}
                <Badge variant={isPrimary ? 'default' : 'secondary'} className={`ml-1.5 text-xs ${isPrimary ? '' : 'bg-amber-100 text-amber-800 hover:bg-amber-100 dark:bg-amber-900/30 dark:text-amber-400'}`}>
                  {isPrimary ? 'Primary Phase' : 'Fallback Phase'}
                </Badge>
              </CardTitle>
              <CardDescription className="mt-0.5">
                {status.label || 'Active configuration'} · Effective from{' '}
                {formatDate(status.effective_from)}
              </CardDescription>
            </div>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold tracking-tight">{status.total_percentage}%</div>
            <div className="text-xs text-muted-foreground">Total share</div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {status.participants.map((p) => (
            <div
              key={p.client_id}
              className="flex items-center justify-between rounded-lg border bg-background/80 px-3 py-2.5"
            >
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium">{p.client_name}</div>
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  {p.fee_sharing_type === 'percentage' ? (
                    <>
                      <Percent className="h-3 w-3" />
                      {p.percentage_share}% share
                    </>
                  ) : (
                    <>
                      <Euro className="h-3 w-3" />
                      {formatCurrency(p.fixed_amount)} cap
                      {p.installments && p.installments > 1 && (
                        <span className="ml-1">· {p.installments} installments</span>
                      )}
                    </>
                  )}
                </div>
              </div>
              {p.fee_sharing_type === 'fixed_amount' && (
                <div className="ml-3 text-right">
                  {p.is_exhausted ? (
                    <Badge variant="secondary" className="bg-red-100 text-red-700 text-[10px] dark:bg-red-900/30 dark:text-red-400">
                      Exhausted
                    </Badge>
                  ) : (
                    <span className="text-xs font-medium text-emerald-600 dark:text-emerald-400">
                      {formatCurrency(p.fixed_amount_remaining)} left
                    </span>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Config Card (accordion-style) ─────────────────────────────────────────────

function ConfigCard({
  config,
  clients,
  isExpanded,
  onToggle,
  onEdit,
  onDelete,
  onReset,
}: {
  config: FeeSharingConfig;
  clients: Client[];
  isExpanded: boolean;
  onToggle: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onReset: () => void;
}) {
  const hasFixedAmount = config.participants?.some((p) => p.fee_sharing_type === 'fixed_amount');
  const allExhausted =
    hasFixedAmount &&
    config.participants
      ?.filter((p) => p.fee_sharing_type === 'fixed_amount')
      .every((p) => p.is_exhausted);
  const percentageTotal = config.participants
    ?.filter((p) => p.fee_sharing_type === 'percentage')
    .reduce((sum, p) => sum + parseFloat(p.percentage_share || '0'), 0);

  const isFuture = new Date(config.effective_from) > new Date();

  return (
    <div className={`rounded-lg border transition-colors ${config.is_active ? 'border-primary/40 bg-primary/[0.02]' : isFuture ? 'border-dashed border-muted-foreground/30' : 'border-border'}`}>
      {/* Header */}
      <button
        type="button"
        className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-muted/50 transition-colors rounded-t-lg"
        onClick={onToggle}
      >
        {isExpanded ? (
          <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
        )}
        <div className="flex flex-1 items-center gap-3 min-w-0">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="font-medium truncate">
                {config.label || `Configuration from ${formatDate(config.effective_from)}`}
              </span>
              {config.is_active && (
                <Badge className="bg-emerald-100 text-emerald-700 text-[10px] shrink-0 dark:bg-emerald-900/30 dark:text-emerald-400">
                  <CheckCircle2 className="h-3 w-3 mr-1" />
                  Active
                </Badge>
              )}
              {isFuture && (
                <Badge variant="outline" className="text-[10px] shrink-0">
                  <Clock className="h-3 w-3 mr-1" />
                  Scheduled
                </Badge>
              )}
              {allExhausted && (
                <Badge variant="secondary" className="bg-amber-100 text-amber-700 text-[10px] shrink-0 dark:bg-amber-900/30 dark:text-amber-400">
                  <AlertTriangle className="h-3 w-3 mr-1" />
                  Fallback
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-3 mt-0.5 text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                {formatDate(config.effective_from)}
              </span>
              <span className="flex items-center gap-1">
                <Users className="h-3 w-3" />
                {config.participants?.length || 0} participant{(config.participants?.length || 0) !== 1 ? 's' : ''}
              </span>
              {percentageTotal != null && (
                <span>{Math.round(percentageTotal * 100) / 100}% allocated</span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
          {hasFixedAmount && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onReset}>
                    <RotateCcw className="h-3.5 w-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Reset fixed amount usage</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onEdit}>
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Edit configuration</TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" onClick={onDelete}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Delete configuration</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </button>

      {/* Expanded Content */}
      {isExpanded && config.participants && config.participants.length > 0 && (
        <div className="border-t px-4 pb-4 pt-3">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="text-xs">Client</TableHead>
                <TableHead className="text-xs">Type</TableHead>
                <TableHead className="text-xs text-right">Share / Cap</TableHead>
                <TableHead className="text-xs text-right">Installments</TableHead>
                <TableHead className="text-xs text-right">Per Invoice</TableHead>
                <TableHead className="text-xs text-right">Used</TableHead>
                <TableHead className="text-xs text-right">Remaining</TableHead>
                <TableHead className="text-xs text-center">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {config.participants.map((p, idx) => (
                <TableRow key={p.id ?? idx} className="hover:bg-muted/30">
                  <TableCell className="font-medium text-sm py-2.5">
                    {getClientName(clients, p.client)}
                  </TableCell>
                  <TableCell className="py-2.5">
                    <Badge variant="outline" className="text-[10px] font-normal">
                      {p.fee_sharing_type === 'percentage' ? (
                        <><Percent className="h-3 w-3 mr-1" />Percentage</>
                      ) : (
                        <><Euro className="h-3 w-3 mr-1" />Fixed Amount</>
                      )}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right text-sm py-2.5 font-medium">
                    {p.fee_sharing_type === 'percentage'
                      ? `${p.percentage_share}%`
                      : formatCurrency(p.fixed_amount)}
                  </TableCell>
                  <TableCell className="text-right text-sm py-2.5 text-muted-foreground">
                    {p.fee_sharing_type === 'fixed_amount'
                      ? (p.installments ?? 1)
                      : '—'}
                  </TableCell>
                  <TableCell className="text-right text-sm py-2.5 text-muted-foreground">
                    {p.fee_sharing_type === 'fixed_amount'
                      ? formatCurrency(p.amount_per_installment)
                      : '—'}
                  </TableCell>
                  <TableCell className="text-right text-sm py-2.5 text-muted-foreground">
                    {p.fee_sharing_type === 'fixed_amount'
                      ? formatCurrency(p.fixed_amount_used)
                      : '—'}
                  </TableCell>
                  <TableCell className="text-right text-sm py-2.5">
                    {p.fee_sharing_type === 'fixed_amount' ? (
                      <span className={p.is_exhausted ? 'text-destructive font-medium' : 'text-emerald-600 dark:text-emerald-400 font-medium'}>
                        {formatCurrency(p.fixed_amount_remaining)}
                      </span>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell className="text-center py-2.5">
                    {p.fee_sharing_type === 'fixed_amount' ? (
                      p.is_exhausted ? (
                        <Badge variant="secondary" className="bg-red-100 text-red-700 text-[10px] dark:bg-red-900/30 dark:text-red-400">Exhausted</Badge>
                      ) : (
                        <Badge variant="secondary" className="bg-emerald-100 text-emerald-700 text-[10px] dark:bg-emerald-900/30 dark:text-emerald-400">Active</Badge>
                      )
                    ) : (
                      <Badge variant="secondary" className="text-[10px]">Active</Badge>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

// ─── Empty State ───────────────────────────────────────────────────────────────

function EmptyState({ onAdd }: { onAdd: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-muted">
        <Shield className="h-7 w-7 text-muted-foreground" />
      </div>
      <h3 className="mt-4 text-sm font-semibold">No fee sharing configurations</h3>
      <p className="mt-1.5 max-w-sm text-sm text-muted-foreground">
        Set up date-based fee sharing to split billing between multiple clients with percentage or
        fixed-amount allocations.
      </p>
      <Button className="mt-5" size="sm" onClick={onAdd}>
        <Plus className="h-4 w-4 mr-1.5" />
        Add a configuration
      </Button>
    </div>
  );
}

// ─── Form Dialog ───────────────────────────────────────────────────────────────

interface ParticipantFormEntry {
  id?: number;
  client: number | null;
  fee_sharing_type: FeeSharingType;
  percentage_share: string;
  fixed_amount: string;
  installments: number;
}

interface FeeSharingFormDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  matterId: number;
  existingConfig: FeeSharingConfig | null;
  clients: Client[];
  existingDates: string[];
}

function FeeSharingFormDialog({
  isOpen,
  onClose,
  onSuccess,
  matterId,
  existingConfig,
  clients,
  existingDates,
}: FeeSharingFormDialogProps) {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [label, setLabel] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [participants, setParticipants] = useState<ParticipantFormEntry[]>([]);

  // Reset form when dialog opens
  useEffect(() => {
    if (isOpen) {
      if (existingConfig) {
        setLabel(existingConfig.label || '');
        setEffectiveFrom(existingConfig.effective_from || '');
        setParticipants(
          (existingConfig.participants || []).map((p) => ({
            id: p.id,
            client: p.client,
            fee_sharing_type: p.fee_sharing_type,
            percentage_share: p.percentage_share ?? '0.00',
            fixed_amount: p.fixed_amount ?? '',
            installments: p.installments ?? 1,
          }))
        );
      } else {
        setLabel('');
        setEffectiveFrom('');
        setParticipants([
          { client: null, fee_sharing_type: 'percentage', percentage_share: '100.00', fixed_amount: '', installments: 1 },
        ]);
      }
    }
  }, [isOpen, existingConfig]);

  const addParticipant = () => {
    setParticipants((prev) => [
      ...prev,
      { client: null, fee_sharing_type: 'percentage', percentage_share: '0.00', fixed_amount: '', installments: 1 },
    ]);
  };

  const removeParticipant = (index: number) => {
    if (participants.length <= 1) return;
    setParticipants((prev) => prev.filter((_, i) => i !== index));
  };

  const updateParticipant = (index: number, field: keyof ParticipantFormEntry, value: any) => {
    setParticipants((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      // When switching type, reset the irrelevant field
      if (field === 'fee_sharing_type') {
        if (value === 'percentage') {
          updated[index].fixed_amount = '';
          updated[index].installments = 1;
        } else {
          updated[index].percentage_share = '0.00';
        }
      }
      return updated;
    });
  };

  const distributeEqually = () => {
    const pctParticipants = participants.filter((p) => p.fee_sharing_type === 'percentage');
    if (pctParticipants.length === 0) return;

    const share = Math.floor(10000 / pctParticipants.length) / 100;
    const remainder = (100 - share * pctParticipants.length);
    let distributed = 0;

    setParticipants((prev) =>
      prev.map((p) => {
        if (p.fee_sharing_type !== 'percentage') return p;
        let val = share;
        if (distributed < remainder) {
          val += 0.01;
          distributed += 0.01;
        }
        return { ...p, percentage_share: val.toFixed(2) };
      })
    );
  };

  const percentageSum = participants
    .filter((p) => p.fee_sharing_type === 'percentage')
    .reduce((s, p) => s + parseFloat(p.percentage_share || '0'), 0);

  const getUsedClientIds = (currentIndex: number) =>
    participants.filter((_, i) => i !== currentIndex).map((p) => p.client).filter(Boolean);

  const validate = (): string | null => {
    if (!effectiveFrom) return 'Effective date is required';

    // Check unique date (exclude current config's date)
    const existingDateSet = existingDates.filter(
      (d) => !(existingConfig && d === existingConfig.effective_from)
    );
    if (existingDateSet.includes(effectiveFrom))
      return 'A configuration with this effective date already exists';

    if (participants.length === 0) return 'At least one participant is required';

    for (const p of participants) {
      if (!p.client) return 'All participants must have a client selected';
      if (p.fee_sharing_type === 'fixed_amount') {
        const amt = parseFloat(p.fixed_amount);
        if (!p.fixed_amount || isNaN(amt) || amt <= 0) return 'Fixed amount must be a positive value';
        if (p.installments < 1) return 'Number of installments must be at least 1';
      }
    }

    // Check unique clients
    const clientIds = participants.map((p) => p.client);
    const uniqueClients = new Set(clientIds);
    if (uniqueClients.size !== clientIds.length) return 'Each client can only appear once per configuration';

    // Check percentage sum
    if (Math.abs(percentageSum - 100) > 0.01) return `Percentage shares must sum to 100% (currently ${percentageSum.toFixed(2)}%)`;

    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const error = validate();
    if (error) {
      toast({ title: 'Validation Error', description: error, variant: 'destructive' });
      return;
    }

    const payload: FeeSharingConfigPayload = {
      ...(existingConfig?.id ? { id: existingConfig.id } : {}),
      label,
      effective_from: effectiveFrom,
      participants: participants.map((p) => ({
        ...(p.id ? { id: p.id } : {}),
        client: p.client!,
        fee_sharing_type: p.fee_sharing_type,
        ...(p.fee_sharing_type === 'percentage'
          ? { percentage_share: p.percentage_share }
          : { fixed_amount: p.fixed_amount, installments: p.installments }),
      })),
    };

    setIsSubmitting(true);
    try {
      if (existingConfig?.id) {
        await updateFeeSharingConfig(matterId, existingConfig.id, payload);
        toast({ title: 'Success', description: 'Fee sharing configuration updated' });
      } else {
        await createFeeSharingConfig(matterId, payload);
        toast({ title: 'Success', description: 'Fee sharing configuration created' });
      }
      onSuccess();
    } catch (err: any) {
      const msg =
        err.response?.data?.detail ||
        err.response?.data?.non_field_errors?.[0] ||
        (typeof err.response?.data === 'string' ? err.response.data : null) ||
        'Failed to save configuration';
      toast({ title: 'Error', description: msg, variant: 'destructive' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            {existingConfig ? 'Edit Fee Sharing' : 'New Fee Sharing Configuration'}
          </DialogTitle>
          <DialogDescription>
            Define how billing is split between clients. Percentage shares must total 100%.
          </DialogDescription>
        </DialogHeader>

        <div className="overflow-y-auto pr-4 -mr-4 max-h-[calc(90vh-200px)]">
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Meta fields */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="fs-label" className="font-medium">
                  Label
                </Label>
                <Input
                  id="fs-label"
                  placeholder='e.g. "Phase 1 — with Company C"'
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="fs-date" className="font-medium">
                  Start Date *
                </Label>
                <DatePicker
                  id="fs-date"
                  value={effectiveFrom}
                  onChange={setEffectiveFrom}
                />
              </div>
            </div>

            <Separator />

            {/* Participants */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Label className="text-base font-medium">Clients</Label>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs">
                        <p>
                          Each client receives a share of the billing.
                          Percentage shares must total exactly 100%.
                          Fixed-amount clients have a budget cap that can be split into installments.
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>

                <div className="flex items-center gap-2">
                  <span
                    className={`text-xs font-medium ${Math.abs(percentageSum - 100) > 0.01 ? 'text-destructive' : 'text-emerald-600 dark:text-emerald-400'}`}
                  >
                    {percentageSum.toFixed(1)}% / 100%
                  </span>
                  {participants.filter((p) => p.fee_sharing_type === 'percentage').length > 1 && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-7 text-xs"
                      onClick={distributeEqually}
                    >
                      Equal Split
                    </Button>
                  )}
                </div>
              </div>

              <div className="space-y-3">
                {participants.map((entry, index) => {
                  const usedIds = getUsedClientIds(index);
                  const availableClients = clients.filter(
                    (c) => !usedIds.includes(c.id) || c.id === entry.client
                  );

                  return (
                    <div
                      key={index}
                      className="rounded-lg border bg-muted/20 p-4 space-y-3 transition-colors hover:bg-muted/30"
                    >
                      {/* Row 1: Client + Type + Remove */}
                      <div className="flex items-start gap-3">
                        <div className="flex-1 space-y-1">
                          <Label className="text-xs text-muted-foreground">Client *</Label>
                          <ClientCombobox
                            value={entry.client || null}
                            onChange={(id) => id && updateParticipant(index, 'client', id)}
                            clients={availableClients}
                            placeholder="Select client…"
                          />
                        </div>

                        <div className="w-44 space-y-1">
                          <Label className="text-xs text-muted-foreground">Type *</Label>
                          <Select
                            value={entry.fee_sharing_type}
                            onValueChange={(val) =>
                              updateParticipant(index, 'fee_sharing_type', val as FeeSharingType)
                            }
                          >
                            <SelectTrigger className="bg-background">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="percentage">Percentage</SelectItem>
                              <SelectItem value="fixed_amount">Fixed Amount</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 mt-5 text-muted-foreground hover:text-destructive"
                          onClick={() => removeParticipant(index)}
                          disabled={participants.length <= 1}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>

                      {/* Row 2: Amount fields */}
                      {entry.fee_sharing_type === 'percentage' ? (
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <Label className={`text-xs ${Math.abs(percentageSum - 100) > 0.01 ? 'text-destructive' : 'text-muted-foreground'}`}>
                              Share (%) *
                            </Label>
                            <div className="relative">
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                max="100"
                                placeholder="0.00"
                                value={entry.percentage_share}
                                onChange={(e) =>
                                  updateParticipant(index, 'percentage_share', e.target.value)
                                }
                                className={`pr-8 ${Math.abs(percentageSum - 100) > 0.01 ? 'border-destructive' : ''}`}
                              />
                              <Percent className="absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="grid grid-cols-3 gap-3">
                          <div className="space-y-1">
                            <Label className="text-xs text-muted-foreground">
                              Total Amount (€) *
                            </Label>
                            <div className="relative">
                              <Input
                                type="number"
                                step="0.01"
                                min="0.01"
                                placeholder="0.00"
                                value={entry.fixed_amount}
                                onChange={(e) =>
                                  updateParticipant(index, 'fixed_amount', e.target.value)
                                }
                              />
                              <Euro className="absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
                            </div>
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs text-muted-foreground flex items-center gap-1">
                              Installments
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Info className="h-3 w-3 cursor-help" />
                                  </TooltipTrigger>
                                  <TooltipContent className="max-w-xs">
                                    <p>
                                      Split across multiple invoices.
                                      E.g. €15,000 ÷ 3 = €5,000 per invoice.
                                    </p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            </Label>
                            <Input
                              type="number"
                              step="1"
                              min="1"
                              placeholder="1"
                              value={entry.installments}
                              onChange={(e) =>
                                updateParticipant(index, 'installments', Math.max(1, parseInt(e.target.value) || 1))
                              }
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs text-muted-foreground">Per Invoice</Label>
                            <div className="h-9 flex items-center px-3 rounded-md border bg-muted/50 text-sm font-medium">
                              {entry.fixed_amount && entry.installments >= 1
                                ? formatCurrency(parseFloat(entry.fixed_amount) / entry.installments)
                                : '—'}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={addParticipant}
                disabled={participants.length >= clients.length}
              >
                <Plus className="h-4 w-4 mr-1.5" />
                Add Client
              </Button>
            </div>

            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting && (
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent mr-2" />
                )}
                {existingConfig ? 'Update Configuration' : 'Create Configuration'}
              </Button>
            </DialogFooter>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
